/**
 * One step's plain fields on a phone and an iPad: named after what the guest sees, in the order they see it. It edits a
 * copy and hands the whole flow back on Save, so Cancel leaves the step as it was.
 */
import { type ReactNode, useId, useState } from "react";
import { tagCanComeOff } from "../../shared/tags";
import type { BranchAsk, TriggerJson } from "../../shared/types";
import { type Editor, FlowTag, kindLabel, sendWebhookTest, TagsContext, useFlowTags } from "../components/FlowEditor";
import { RichBox } from "../components/RichBox";
import { Button } from "../components/ui/Button";
import { Seg } from "../components/ui/Seg";
import { useToast } from "../components/ui/Toast";
import { locateStep, mapStep, type Step, unfinished } from "./flow-model";

/** What is being edited: the When stop ("trigger"), or a step by its id. */
type StepTarget = string;

export function targetTitle(editor: Editor, target: StepTarget): string {
  if (target === "trigger") return "When";
  const at = locateStep(editor.steps, target);
  return at ? kindLabel(at.step.type) : "Step";
}

function Field({ label, htmlFor, children }: { label: string; htmlFor?: string; children: ReactNode }) {
  return (
    <div className="field">
      <label htmlFor={htmlFor}>{label}</label>
      {children}
    </div>
  );
}

function TextField({ label, value, onChange, placeholder, url }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string; url?: boolean }) {
  const id = useId();
  return (
    <Field label={label} htmlFor={id}>
      <input dir="auto"
        id={id}
        className="inp"
        value={value}
        placeholder={placeholder}
        autoComplete="off"
        autoCapitalize="off"
        {...(url ? { type: "url", inputMode: "url" as const, spellCheck: false } : {})}
        onChange={(e) => onChange(e.target.value)}
      />
    </Field>
  );
}

function AreaField({ label, value, onChange, rows }: { label: string; value: string; onChange: (v: string) => void; rows: number }) {
  const id = useId();
  return (
    <Field label={label} htmlFor={id}>
      <textarea dir="auto" id={id} className="inp" rows={rows} value={value} onChange={(e) => onChange(e.target.value)} />
    </Field>
  );
}

/** A number box that keeps what is typed until it is a real number, so a cleared box does not jump to 0. */
function NumberField({ label, value, onChange, least = 0 }: { label: string; value: number; onChange: (n: number) => void; least?: number }) {
  const id = useId();
  const [text, setText] = useState(String(value));
  return (
    <Field label={label} htmlFor={id}>
      <input
        id={id}
        className="inp"
        type="number"
        inputMode="numeric"
        min={least}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          onChange(Math.max(least, Number(e.target.value) || 0));
        }}
      />
    </Field>
  );
}

function PickField<T extends string>({ label, value, onChange, options }: { label: string; value: T; onChange: (v: T) => void; options: ReadonlyArray<{ value: T; label: string }> }) {
  const id = useId();
  return (
    <Field label={label} htmlFor={id}>
      <select id={id} className="inp" value={value} onChange={(e) => onChange(e.target.value as T)}>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </Field>
  );
}

function SegField<T extends string>({ label, value, onChange, options }: { label: string; value: T; onChange: (v: T) => void; options: ReadonlyArray<{ value: T; label: string }> }) {
  return (
    <Field label={label}>
      <Seg className="au-seg" label={label} options={options} value={value} onChange={onChange} />
    </Field>
  );
}

function TagField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <Field label={label}>
      <FlowTag label={label} value={value} onChange={onChange} />
    </Field>
  );
}

const ASKS: ReadonlyArray<{ value: BranchAsk["kind"]; label: string }> = [
  { value: "tag", label: "has the tag" },
  { value: "notag", label: "doesn’t have the tag" },
  { value: "pct", label: "Watched %" },
  { value: "min", label: "Minutes watched" },
  { value: "idle", label: "Days since last active" },
];
const COMPARES: ReadonlyArray<{ value: NonNullable<BranchAsk["cmp"]>; label: string }> = [
  { value: "more", label: "more than" },
  { value: "less", label: "less than" },
  { value: "between", label: "between" },
];

function CheckFields({ ask, onChange }: { ask: BranchAsk; onChange: (a: BranchAsk) => void }) {
  const tagAsk = ask.kind === "tag" || ask.kind === "notag";
  const scale = ask.kind === "pct" || ask.kind === "min";
  return (
    <>
      <PickField label="Ask about" value={ask.kind} options={ASKS} onChange={(kind) => onChange({ ...ask, kind })} />
      {tagAsk ? <TagField label="The tag to ask about" value={ask.tag ?? ""} onChange={(tag) => onChange({ ...ask, tag })} /> : null}
      {scale ? (
        <>
          <PickField label="Compare" value={ask.cmp ?? "more"} options={COMPARES} onChange={(cmp) => onChange({ ...ask, cmp })} />
          <NumberField label="The number" value={ask.a ?? 0} onChange={(a) => onChange({ ...ask, a })} />
          {ask.cmp === "between" ? <NumberField label="The second number" value={ask.b ?? 0} onChange={(b) => onChange({ ...ask, b })} /> : null}
        </>
      ) : null}
      {ask.kind === "idle" ? <NumberField label="Days" value={ask.a ?? 0} onChange={(a) => onChange({ ...ask, a })} /> : null}
    </>
  );
}

function TriggerFields({ trigger, onChange }: { trigger: TriggerJson; onChange: (t: TriggerJson) => void }) {
  return (
    <>
      <SegField
        label="When someone"
        value={trigger.type}
        options={[
          { value: "tag_added", label: "gets the tag" },
          { value: "tag_removed", label: "loses the tag" },
        ]}
        onChange={(type) => onChange({ type, value: trigger.value })}
      />
      <TagField label="The tag that starts it" value={trigger.value} onChange={(value) => onChange({ ...trigger, value })} />
      {trigger.type === "tag_added" && tagCanComeOff(trigger.value) ? (
        <SegField
          label="If they lose it, this automation"
          value={trigger.keep_going ? "keep" : "end"}
          options={[
            { value: "end", label: "ends on its own" },
            { value: "keep", label: "keeps going" },
          ]}
          onChange={(v) =>
            onChange(v === "keep" ? { ...trigger, keep_going: true } : { type: trigger.type, value: trigger.value, ...(trigger.how_often ? { how_often: trigger.how_often } : {}) })
          }
        />
      ) : null}
    </>
  );
}

function StepFields({ step, onChange, onToast }: { step: Step; onChange: (s: Step) => void; onToast: (m: string) => void }) {
  const patch = (p: Partial<Step>) => onChange({ ...step, ...p } as Step);
  switch (step.type) {
    case "email":
      return (
        <>
          <TextField label="Subject" value={step.subject} onChange={(subject) => patch({ subject })} />
          <Field label="Message">
            <RichBox bare className="inp fe-msg" value={step.previewHtml} onChange={(previewHtml) => patch({ previewHtml })} ariaLabel="Message" placeholder="" />
          </Field>
          <TextField label="Button" value={step.button} placeholder="Optional" onChange={(button) => patch({ button })} />
        </>
      );
    case "wait": {
      const until = step.mode === "before";
      return (
        <>
          <SegField
            label="For or until"
            value={until ? "before" : "after"}
            options={[
              { value: "after", label: "For" },
              { value: "before", label: "Until" },
            ]}
            onChange={(mode) => patch({ mode, num: mode === "after" ? Math.max(1, step.num) : step.num })}
          />
          <NumberField key={step.mode} label="How many" value={step.num} least={until ? 0 : 1} onChange={(num) => patch({ num })} />
          <SegField
            label="Unit"
            value={step.unit}
            options={[
              { value: "minutes", label: "minutes" },
              { value: "hours", label: "hours" },
              { value: "days", label: "days" },
            ]}
            onChange={(unit) => patch({ unit })}
          />
          <p className="au-hint">{until ? "before the session starts" : "after the step before"}</p>
        </>
      );
    }
    case "branch":
      return <CheckFields ask={step.ask} onChange={(ask) => patch({ ask })} />;
    case "fact":
      return (
        <>
          <SegField
            label="Add or remove"
            value={step.op}
            options={[
              { value: "add", label: "Add" },
              { value: "remove", label: "Remove" },
            ]}
            onChange={(op) => patch({ op })}
          />
          <TagField label="The tag to change" value={step.name} onChange={(name) => patch({ name })} />
        </>
      );
    case "webhook":
      return (
        <>
          <TextField label="Send to this address" url placeholder="https://" value={step.url} onChange={(url) => patch({ url })} />
          <Button className="au-test" icon="send" onClick={() => sendWebhookTest(step.url, onToast)}>
            Send a test
          </Button>
        </>
      );
    case "notify":
      return (
        <>
          <TextField label="To" value={step.email} placeholder="you@yourcompany.com, someone@else.com" onChange={(email) => patch({ email })} />
          <TextField label="Subject" value={step.subject} placeholder="Update on {{first name}}" onChange={(subject) => patch({ subject })} />
          <AreaField label="Message" rows={4} value={step.message} onChange={(message) => patch({ message })} />
        </>
      );
    case "room":
      return (
        <>
          <TextField label="Title" value={step.title} onChange={(title) => patch({ title })} />
          <Field label="Note">
            <RichBox bare className="inp fe-msg short" value={step.noteHtml} onChange={(noteHtml) => patch({ noteHtml })} ariaLabel="Note" placeholder="" />
          </Field>
          <label className="fe-check">
            <input type="checkbox" checked={step.notifyEnabled} onChange={(e) => patch({ notifyEnabled: e.target.checked })} />
            Email them a link
          </label>
          {step.notifyEnabled ? <TextField label="Its subject" value={step.notifySubject} placeholder={step.title} onChange={(notifySubject) => patch({ notifySubject })} /> : null}
        </>
      );
  }
}

/**
 * The form for one step and the Cancel and Save that go with it. The caller puts `fields` in a sheet or a pane and
 * `buttons` where its buttons go; `save` writes the whole flow.
 */
export function useStepEditor(editor: Editor, target: StepTarget, save: (next: Editor) => Promise<void>, close: () => void) {
  const toast = useToast();
  const tags = useFlowTags(editor.trigger, editor.stopWhen, editor.steps);
  const [trigger, setTrigger] = useState(editor.trigger);
  const [step, setStep] = useState<Step | null>(target === "trigger" ? null : (locateStep(editor.steps, target)?.step ?? null));
  const [saving, setSaving] = useState(false);

  const onSave = async () => {
    const next: Editor = target === "trigger" ? { ...editor, trigger } : step ? { ...editor, steps: mapStep(editor.steps, target, () => step) } : editor;
    // Why: the server refuses an unfinished email; say what is missing instead of its error.
    const unready = unfinished(next.trigger, next.steps, target);
    if (unready) {
      toast({ text: unready.words, icon: "info" });
      return;
    }
    setSaving(true);
    try {
      await save(next);
    } finally {
      setSaving(false);
    }
  };

  const fields = (
    <TagsContext.Provider value={tags}>
      <div className="k-form au-form">
        {target === "trigger" ? <TriggerFields trigger={trigger} onChange={setTrigger} /> : step ? <StepFields step={step} onChange={setStep} onToast={(m) => toast(m)} /> : null}
      </div>
    </TagsContext.Provider>
  );
  const cancel = <Button onClick={close}>Cancel</Button>;
  const saveButton = (
    <Button variant="p" disabled={saving} onClick={() => void onSave()}>
      Save
    </Button>
  );
  return { fields, cancel, save: saveButton };
}
